import { useState } from "react";
import { Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
const mesLogo = process.env.PUBLIC_URL + "/mes-logo.png";

export default function Login() {
    const { user, login, error } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);

    if (user) return <Navigate to="/heute" replace />;

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try { await login(email.trim(), password); } catch (_e) { /* handled via context error */ }
        setLoading(false);
    };

    return (
        <div className="grid min-h-screen grid-cols-1 md:grid-cols-5">
            {/* Left / hero panel */}
            <div className="relative hidden overflow-hidden bg-mes-ink md:col-span-3 md:block">
                <div className="absolute inset-0">
                    <img
                        src="https://images.unsplash.com/photo-1668097613572-40b7c11c8727?auto=format&fit=crop&w=1600&q=60"
                        alt=""
                        className="h-full w-full object-cover opacity-35"
                    />
                    <div className="absolute inset-0 bg-gradient-to-br from-mes-ink/85 via-mes-ink/70 to-[#0d3438]/85" style={{ background: "linear-gradient(135deg, rgba(22,35,42,.9) 0%, rgba(22,76,81,.85) 100%)" }} />
                </div>
                <div className="relative z-10 flex h-full flex-col justify-between p-12">
                    <div className="self-start rounded-xl bg-white p-4 shadow-lg">
                        <img
                            data-testid="logo-slot"
                            src={mesLogo}
                            alt="MES – My Easy Solar"
                            className="mes-login-logo block h-auto w-auto"
                            style={{ maxHeight: 72, maxWidth: 320 }}
                        />
                    </div>
                    <div className="max-w-lg">
                        <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-white/60">
                            Zeiterfassung · Bautagebuch · Reporting
                        </div>
                        <h1 className="mt-3 font-display text-4xl font-extrabold leading-tight text-white">
                            Ordentliche Baustellen&shy;dokumentation. Ohne Papier.
                        </h1>
                        <p className="mt-4 max-w-md text-[15px] text-white/70">
                            Zeitkontingente im Blick, Bautagesberichte in Minuten,
                            saubere Kunden-PDFs auf Knopfdruck.
                        </p>
                    </div>
                    <div className="text-[11px] text-white/40">© MES – My Easy Solar</div>
                </div>
            </div>

            {/* Right / form panel */}
            <div className="flex items-center justify-center bg-mes-bg px-6 py-10 md:col-span-2">
                <div className="w-full max-w-sm">
                    <div className="mb-8 flex flex-col items-start gap-3 md:hidden">
                        <div className="rounded-lg border border-mes-border bg-white p-3 shadow-sm">
                            <img
                                data-testid="logo-slot"
                                src={mesLogo}
                                alt="MES – My Easy Solar"
                                className="block h-auto w-auto"
                                style={{ maxHeight: 48, maxWidth: 220 }}
                            />
                        </div>
                        <div>
                            <div className="text-[11px] font-medium uppercase tracking-widest text-mes-muted">Baustellenjournal</div>
                        </div>
                    </div>
                    <h2 className="font-display text-2xl font-bold text-mes-ink">Anmelden</h2>
                    <p className="mt-1 text-sm text-mes-muted">
                        Mit Ihrer Firmen-E-Mail-Adresse.
                    </p>
                    <form onSubmit={submit} className="mt-6 space-y-4" data-testid="login-form">
                        <div className="space-y-1.5">
                            <Label htmlFor="email" className="text-[13px] text-mes-text">E-Mail</Label>
                            <Input
                                id="email" data-testid="login-email"
                                type="email" autoComplete="email" required
                                value={email} onChange={(e) => setEmail(e.target.value)}
                                className="h-12 border-mes-border bg-white text-[15px] focus-visible:ring-1 focus-visible:ring-mes-brand focus-visible:ring-offset-0"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="password" className="text-[13px] text-mes-text">Passwort</Label>
                            <Input
                                id="password" data-testid="login-password"
                                type="password" autoComplete="current-password" required
                                value={password} onChange={(e) => setPassword(e.target.value)}
                                className="h-12 border-mes-border bg-white text-[15px] focus-visible:ring-1 focus-visible:ring-mes-brand focus-visible:ring-offset-0"
                            />
                        </div>
                        {error && (
                            <div
                                data-testid="login-error"
                                className="rounded-md border border-mes-danger/30 bg-mes-danger/5 px-3 py-2 text-sm text-mes-danger"
                            >
                                {error}
                            </div>
                        )}
                        <Button
                            data-testid="login-submit" type="submit" disabled={loading}
                            className="mes-btn-primary h-12 w-full text-[15px]"
                        >
                            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Anmelden"}
                        </Button>
                    </form>
                    <div className="mt-6 text-[11px] text-mes-muted">
                        Zugangsdaten erhalten Sie von Ihrer Bauleitung.
                    </div>
                </div>
            </div>
        </div>
    );
}
